import React, { useContext } from 'react';
import { Issue } from 'api/api.types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ApiContext from 'api/ApiContext';
import { getInternalUrl } from 'utils/routing';
import PollTeaserResult from './PollTeaserResult';

interface Props {
  poll: Issue;
  headingLevel?: 3 | 4;
}

const PollTeaser = ({ poll, headingLevel }: Props) => {
  const { t } = useTranslation('wap');
  const { language } = useContext(ApiContext);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { yes_votes_percentage, no_votes_percentage } = poll.results?.poll_data || {};

  const isFinal = poll.progress?.final;
  const isResultYes = yes_votes_percentage > no_votes_percentage;
  const majorityResult = isResultYes ? yes_votes_percentage : no_votes_percentage;

  const { subtype } = poll.info.poll_info;
  const yesI18nKey = subtype === 'tie_break' ? 'vi' : 'yes';
  const noI18nKey = subtype === 'tie_break' ? 'ge' : 'no';

  const label = isResultYes ? t(yesI18nKey) : t(noI18nKey);

  const title = (poll.info.short_title || poll.info.title)?.[language];
  const hasResult = !!poll.results?.poll_data.yes_votes;

  const getJuristictionLabel = () => {
    switch (poll.info.scope) {
      case 'cantonal':
        return t('cantonal_poll');
      case 'federal':
        return t('federal_poll');
      default:
        return '';
    }
  };

  const HeadingTag = `h${headingLevel}` as 'h3' | 'h4';

  return (
    <Link
      to={getInternalUrl(poll.info, 'poll')}
      className={classNames('pollteaser', {
        'pollteaser--with-results': hasResult,
        'pollteaser--yes': isResultYes && isFinal,
        'pollteaser--no': !isResultYes && isFinal,
      })}
    >
      <div className="pollteaser__inner">
        <HeadingTag className="pollteaser__heading">
          {/* eslint-disable-next-line react/no-danger */}
          <span dangerouslySetInnerHTML={{ __html: title }} />
        </HeadingTag>
        <div className="pollteaser__jurisdiction">{getJuristictionLabel()}</div>
        {hasResult && <PollTeaserResult label={label} majorityResult={majorityResult} />}
      </div>
    </Link>
  );
};

PollTeaser.defaultProps = {
  headingLevel: 3,
};

export default PollTeaser;
